import { useTranslations } from 'use-intl';
import { toast } from 'react-toastify';
import { RichText } from 'prismic-reactjs';
import SidePanel from '@monofront/design-system/organisms/SidePanel';
import { useFormatProductWeight } from '@monofront/business/hooks/product/useFormatProductWeight';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import {
  useCreateProductReview,
  useProductLayout,
} from '@monofront/business/hooks';
import { Controller, useForm } from 'react-hook-form';
import { useAuthentication } from '@context/businessContainer';
import { useEffect, useState } from 'react';
import Button from '@monofront/design-system/components/Button';
import Label from '@monofront/design-system/components/Form/Label';
import Textarea from '@monofront/design-system/components/Form/Textarea';
import ReviewStarsSelect from '@components/ReviewStarsSelect';
import { IProduct, ITrackReviewProductEvent } from '@monofront/types';
import { dataLayerTrackEvent } from '@utils/tracking';
import { getProductCategory } from '@monofront/business/utils';
import { trackEvent } from '@monofront/tracking';
import { ModalConfirmation } from '@components/ModalConfirmation';
import Star from '@monofront/design-system/visuals/Icons/Star';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import Alert from '@monofront/design-system/visuals/Icons/Alert';

interface IProductReviewsPanelProps {
  product: IProduct;
  isOpen: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

const ProductReviewsPanel: React.FC<IProductReviewsPanelProps> = ({
  product,
  isOpen,
  onSuccess,
  onCancel,
}) => {
  const tReviews = useTranslations('Reviews');
  const tGlobal = useTranslations('Global');
  const tErrors = useTranslations('Errors');

  const { customer } = useAuthentication();
  const { productLayout } = useProductLayout();

  const { formatProductWeight } = useFormatProductWeight();
  const formattedProductWeight = formatProductWeight(
    product.meta.finance,
    false,
  );

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { error, called, isLoading, createProductReview } =
    useCreateProductReview();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    getValues,
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      rating: undefined as number | undefined,
      comment: undefined as string | undefined,
    },
  });

  const successToastMessage = (
    <p>
      {tReviews.rich('addReviewThanks2', {
        strong: (children) => (
          <strong className="tw-mb-2 tw-block">{children}</strong>
        ),
      })}
    </p>
  );

  useEffect(() => {
    if (!isLoading && called && error) {
      toast(tErrors('addReviewError'), {
        progressClassName: 'toast-error',
      });
    } else if (!isLoading && called && !error) {
      toast(successToastMessage, {
        progressClassName: 'toast-green',
        autoClose: 7000,
      });
      setValue('rating', undefined);

      // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
      dataLayerTrackEvent('productReviewed');

      let eventData: ITrackReviewProductEvent['data'];

      if (product) {
        eventData = {
          products: [
            {
              name: product.title,
              sku: product.sku,
              price: product.price,
              brand: product.vendor,
              category: getProductCategory(product) ?? undefined,
            },
          ],
        };
      }

      trackEvent({ type: 'LFReviewProduct', data: eventData });
      reset();
      onSuccess();
    }
  }, [called, error, isLoading]);

  const triggerOnSubmit = ({ comment, rating }: Record<string, any>) => {
    createProductReview({
      sku: product.sku,
      email: customer!.email,
      rating: rating + 1,
      comment,
    });
  };

  // Register custom component: https://github.com/react-hook-form/react-hook-form/issues/187#issuecomment-518616009
  useEffect(() => {
    register('rating', {
      required: {
        value: true,
        message: tReviews('chooseAReviewGrade'),
      },
    });
  }, [register]);

  const resetAndClosePanel = () => {
    reset();
    setIsCancelModalOpen(false);
    onCancel();
  };

  const onClosePanel = () => {
    if (isDirty) {
      setIsCancelModalOpen(true);
    } else {
      resetAndClosePanel();
    }
  };

  const rating = getValues().rating;

  return (
    <>
      <SidePanel.SidePanel isOpen={isOpen} onClose={() => onClosePanel()}>
        <SidePanel.SidePanelHeader onClose={() => onClosePanel()}>
          <h2 className="title tw-lf-title-sm">{tReviews('panelTitle')}</h2>
        </SidePanel.SidePanelHeader>
        <SidePanel.SidePanelBody UNSAFE_className=" !tw-pt-6 !tw-pb-24">
          <form onSubmit={handleSubmit(triggerOnSubmit)} className="tw-mt-2">
            <p>
              {tReviews('panelProductToReview')}
              <span
                className={`
              tw-mt-2 tw-flex tw-items-center tw-gap-4 tw-text-sm
              [&_span:first-child]:tw-shrink-0 [&_span:first-child]:!tw-rounded-xl
              [&_span:first-child]:!tw-border [&_span:first-child]:!tw-border-solid
              [&_span:first-child]:!tw-border-blue-200`}
              >
                {product.images?.[0] && (
                  <ResponsiveImage
                    src={product.images[0]}
                    layout="fixed"
                    width={60}
                    height={60}
                  />
                )}
                <span>
                  <strong className="tw-mb-2 tw-block tw-font-semibold">
                    {product.title}
                  </strong>
                  <span className="tw-text-black-700">
                    <strong
                      className={`tw-mr-2 tw-inline-block tw-border-0 tw-border-r
                    tw-border-solid tw-border-blue-200 tw-pr-2 tw-font-semibold
                  `}
                    >
                      {formattedProductWeight}
                    </strong>
                    {product.vendor}
                  </span>
                </span>
              </span>
            </p>
            <div className="tw-mt-8">
              <Label id="rating">{tReviews('panelNote')}</Label>
              <div className="tw-mt-2">
                <ReviewStarsSelect
                  ratingSelected={rating ?? null}
                  setRatingSelected={(rating) => {
                    setValue('rating', rating, {
                      shouldValidate: true,
                      shouldTouch: true,
                      shouldDirty: true,
                    });
                  }}
                  size="lg"
                />
                {rating != null ? (
                  <span className="tw-ml-3 tw-align-bottom tw-text-lg tw-font-semibold">{`${
                    rating + 1
                  }/5`}</span>
                ) : null}
              </div>
              {errors.rating?.message && (
                <p className="error tw-lf-error-message tw-mt-2">
                  <Alert
                    size={IconSizes.sm}
                    aria-hidden
                    UNSAFE_className="tw-align-text-top tw-mr-2"
                  />
                  {errors?.rating?.message as string}
                </p>
              )}
            </div>
            <div className="tw-mt-8">
              <Label id="comment">{tReviews('panelComment')}</Label>
              <Controller
                name="comment"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: tReviews('requiredComment'),
                  },
                  maxLength: {
                    value: 500,
                    message: `${tGlobal('nbChars', { nb: 500 })} ${tGlobal(
                      'maximum',
                    )}`,
                  },
                }}
                render={({ field }) => {
                  return (
                    <Textarea
                      id="comment"
                      {...field}
                      placeholder={tReviews('panelCommentPlaceholder')}
                      maxLength={500}
                      counterLimit={500}
                      hasError={Boolean(errors.comment)}
                      UNSAFE_className="tw-mt-2 !tw-min-h-[150px] tw-resize-y"
                    />
                  );
                }}
              />
              {errors.comment && (
                <p className="error tw-lf-error-message tw--mt-5">
                  <Alert
                    size={IconSizes.sm}
                    aria-hidden
                    UNSAFE_className="tw-align-text-top tw-mr-2"
                  />
                  {errors?.comment?.message as string}
                </p>
              )}
            </div>

            {productLayout.reviews_description && (
              <div className="tw-lf-rich-text tw-mt-8 tw-border-0 tw-border-t tw-border-solid tw-border-blue-200 tw-pt-8">
                <RichText render={productLayout.reviews_description} />
              </div>
            )}

            <div className="panel-cta tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-border-0 tw-border-t tw-border-solid tw-border-blue-200 tw-bg-white tw-p-4">
              <Button
                type="submit"
                isLoading={isLoading}
                dataTestId="CreateReviewForm-Submit"
              >
                {tReviews('panelReviewSubmit')}
              </Button>
            </div>
          </form>
        </SidePanel.SidePanelBody>
      </SidePanel.SidePanel>
      <ModalConfirmation
        message={tReviews('panelCancelMessage')}
        isOpen={isCancelModalOpen}
        hasCancelButton
        confirmCancelButtonLabel={tReviews('panelCancelMessageResume')}
        confirmButtonLabel={tReviews('panelCancelMessageConfirm')}
        confirmButtonOnClick={() => resetAndClosePanel()}
        cancelButtonOnClick={() => setIsCancelModalOpen(false)}
        centeredMessage
      >
        <Star size={IconSizes.md} />
      </ModalConfirmation>
      <style jsx>{`
        // fix known issue with z-index on Safari
        .panel-cta {
          transform: translate3d(0, 0, 0);
        }
      `}</style>
    </>
  );
};

export default ProductReviewsPanel;
